import { AssignmentGroupingType } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import type { Control, UseFormReturn, UseFormSetValue } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { AssignmentType } from '@breakoutlearning/firebase-repository/types'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { useTranslationTyped } from 'i18n/i18n'
import classNames from 'classnames'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import type { AssignmentFormValues } from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { PeopleGroup } from 'components/icons/PeopleGroup'
import { TooltipWrapper } from 'pages/instructor/shared/GradingTooltips'
import { Trans } from 'react-i18next'
import { Person } from 'components/icons/Person'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { clickOnEnter, clickOnSpace, composeClickHandlers } from 'util/keyboard'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { useEffect, useMemo } from 'react'
import { PeopleMultiple } from 'components/icons/PeopleMultiple'
import { isPeerReviewAllowed } from '@breakoutlearning/firebase-repository/sources-of-truth/isPeerReviewAllowed'
import { type CreateOrEditAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/CreateOrEditAssignmentCubit'
import { GroupingSizeInput } from './GroupingSizeInput'

export const GroupingInputs = ({
  form,
  groupingType,
  customGroupingSelected,
  forbidStudentGroupCreation,
  forbidStudentGroupScheduling,
  forbidStudentGroupSelection,
  disableGroupingReason,
  isForcedStage,
  readOnly,
  cubit,
}: {
  form: UseFormReturn<AssignmentFormValues>
  groupingType: AssignmentGroupingType
  customGroupingSelected: boolean | undefined
  forbidStudentGroupCreation: boolean | undefined
  forbidStudentGroupScheduling: boolean | undefined
  forbidStudentGroupSelection: boolean | undefined
  disableGroupingReason: string | undefined
  isForcedStage: boolean
  readOnly: boolean
  cubit: CreateOrEditAssignmentCubit
}) => {
  const { tt } = useTranslationTyped()
  const { setValue, watch, control, trigger } = form

  const [isSimpleStudentConfig, isSimpleInstructorConfig] = useMemo(() => {
    if (groupingType === AssignmentGroupingType.automaticRandom)
      return [false, false]

    // order is important here
    const perms = [
      forbidStudentGroupCreation ?? false,
      forbidStudentGroupSelection ?? false,
      forbidStudentGroupScheduling ?? false,
    ]
    const instructorDefaults = [true, true, false]
    const studentDefaults = [false, false, false]

    if (perms.every((perm, i) => perm === studentDefaults[i]))
      return [true, false] as const

    if (perms.every((perm, i) => perm === instructorDefaults[i]))
      return [false, true] as const

    return [false, false] as const
  }, [
    forbidStudentGroupCreation,
    forbidStudentGroupScheduling,
    forbidStudentGroupSelection,
    groupingType,
  ])

  // simple method doesn't matter if showing custom grouping
  const [showCustomGrouping, simpleGroupingMethod] = useMemo(() => {
    if (customGroupingSelected) return [true, 'instructor'] as const

    if (groupingType === AssignmentGroupingType.automaticRandom)
      return [true, 'instructor'] as const

    if (isSimpleStudentConfig) return [false, 'student'] as const
    if (isSimpleInstructorConfig) return [false, 'instructor'] as const

    // if we dont have a match, show custom view
    return [true, 'instructor'] as const
  }, [
    customGroupingSelected,
    groupingType,
    isSimpleInstructorConfig,
    isSimpleStudentConfig,
  ])

  const { minGroupSize, maxGroupSize } = useMemo(() => {
    return {
      minGroupSize: cubit.roleGroupSizeConstraints?.minGroupSize ?? 1,
      maxGroupSize:
        cubit.roleGroupSizeConstraints?.maxGroupSize ??
        RoomState.maxAllowedUsers,
    }
  }, [cubit.roleGroupSizeConstraints])

  // if user has somehow entered a state where their config can not be represented by the simple config options
  // but have not toggled it on, we should toggle it on behalf of them
  useEffect(() => {
    if (showCustomGrouping && !customGroupingSelected)
      setValue('customGroupingSelected', true, { shouldDirty: false })
  }, [setValue, showCustomGrouping, customGroupingSelected])

  // if we're not forbidding creation, we must not forbid group selection
  useEffect(() => {
    if (!forbidStudentGroupCreation)
      setValue('forbidStudentGroupSelection', false, { shouldDirty: false })
  }, [forbidStudentGroupCreation, setValue])

  const groupingSize = watch('groupingSize')
  const peerReviewsEnabled = watch('peerReviewsEnabled')

  useEffect(() => {
    setValue('groupingSizeMaximum', groupingSize, { shouldDirty: false })
    trigger(['groupingSize', 'groupingSizeMinimum', 'groupingSizeMaximum'])
  }, [groupingSize, maxGroupSize, minGroupSize, setValue, trigger])

  return (
    <>
      {!showCustomGrouping && (
        <div className="mb-3.5 flex w-full min-w-[536px] flex-row gap-4 py-[4px]">
          <div
            data-testid="student-led-grouping-button"
            tabIndex={0}
            className={classNames(
              'flex flex-1 flex-col gap-2 rounded-[20px] border px-6 py-[26px] outline-offset-[2px]',
              {
                'border-fixed-accent-color': simpleGroupingMethod === 'student',
                'border-outline-light': simpleGroupingMethod !== 'student',
                'cursor-not-allowed': readOnly,
                'cursor-pointer': !readOnly,
              }
            )}
            onClick={() => {
              if (readOnly) return
              setValue('forbidStudentGroupCreation', false)
              setValue('forbidStudentGroupSelection', false)
              setValue('forbidStudentGroupScheduling', false)
            }}
            onKeyDown={composeClickHandlers(clickOnEnter, clickOnSpace)}
          >
            <PeopleGroup
              size={28}
              className={classNames({
                'stroke-fixed-accent-color': simpleGroupingMethod === 'student',
                'stroke-outline-light': simpleGroupingMethod !== 'student',
              })}
            />
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-1">
                <strong className="text-title-large">
                  {tt.instructor_assignment.groups.student_led_grouping()}
                </strong>
                <BreakoutInfoTooltip
                  placement="bottom"
                  content={
                    <TooltipWrapper>
                      <ul className="list-disc pl-3">
                        <Trans
                          i18nKey="instructor_assignment.configure_assignment_dialog.student_led_grouping_tooltip_list_items"
                          components={{
                            li: <li />,
                          }}
                        />
                      </ul>
                    </TooltipWrapper>
                  }
                />
              </div>
              <strong className="text-body-medium text-on-surface-var">
                {tt.instructor_assignment.configure_assignment_dialog.student_led_grouping_description()}
              </strong>
            </div>
          </div>
          <div
            data-testid="instructor-led-grouping-button"
            tabIndex={0}
            className={classNames(
              'flex flex-1 flex-col gap-2 rounded-[20px] border px-6 py-[26px] outline-offset-2',
              {
                'border-fixed-accent-color':
                  simpleGroupingMethod === 'instructor',
                'border-outline-light': simpleGroupingMethod !== 'instructor',
                'cursor-not-allowed': readOnly,
                'cursor-pointer': !readOnly,
              }
            )}
            onClick={() => {
              if (readOnly) return
              setValue('forbidStudentGroupCreation', true)
              setValue('forbidStudentGroupSelection', true)
              setValue('forbidStudentGroupScheduling', false)
            }}
            onKeyDown={composeClickHandlers(clickOnEnter, clickOnSpace)}
          >
            <Person
              size={28}
              className={classNames({
                'stroke-fixed-accent-color':
                  simpleGroupingMethod === 'instructor',
                'stroke-outline-light': simpleGroupingMethod !== 'instructor',
              })}
            />
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-1">
                <strong className="text-title-large">
                  {tt.instructor_assignment.groups.instructor_led_grouping()}
                </strong>
                <BreakoutInfoTooltip
                  placement="bottom"
                  content={
                    <TooltipWrapper>
                      <ul className="list-disc pl-3">
                        <Trans
                          i18nKey="instructor_assignment.configure_assignment_dialog.instructor_led_grouping_tooltip_list_items"
                          components={{
                            li: <li />,
                          }}
                        />
                      </ul>
                    </TooltipWrapper>
                  }
                />
              </div>
              <strong className="text-body-medium text-on-surface-var">
                {tt.instructor_assignment.configure_assignment_dialog.instructor_led_grouping_description()}
              </strong>
            </div>
          </div>
        </div>
      )}
      {showCustomGrouping && (
        <div className="mb-3.5 flex w-full min-w-[400px] flex-col items-center gap-3 rounded-2xl border border-outline-light p-5 text-body-medium shadow-sm">
          <strong className="text-on-surface-var">
            {tt.instructor_assignment.configure_assignment_dialog.customize_group_settings()}
          </strong>
          <div className="h-[1px] w-full bg-outline-light" />
          {(
            [
              'forbidStudentGroupCreation',
              'forbidStudentGroupSelection',
              'forbidStudentGroupScheduling',
            ] as const
          ).map((k) => {
            // if creation is toggled on, join/leave must be toggled on
            const disableScheduling =
              k === 'forbidStudentGroupSelection' &&
              forbidStudentGroupCreation === false

            const disabled =
              disableScheduling || !!disableGroupingReason || readOnly
            const disabledReason =
              disableGroupingReason ??
              tt.instructor_assignment.groups.disabled_forbid_group_selection_tooltip()

            const label: string = (() => {
              switch (k) {
                case 'forbidStudentGroupSelection': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_join_leave()
                }
                case 'forbidStudentGroupCreation': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_create()
                }
                case 'forbidStudentGroupScheduling': {
                  return tt.instructor_assignment.configure_assignment_dialog.allow_group_meeting_time()
                }
              }
            })()
            return (
              <Controller
                key={k + '_switch'}
                control={control}
                name={k}
                render={({ field }) => {
                  return (
                    <BreakoutTooltip
                      enabled={disabled}
                      placement="right"
                      className="whitespace-pre-wrap"
                      content={disabledReason}
                    >
                      <div className="flex w-full flex-row items-center justify-between">
                        <strong
                          className={classNames('text-body-medium', {
                            'text-on-surface-var': disabled,
                          })}
                        >
                          {label}
                        </strong>
                        <BreakoutSwitch
                          initialValue={
                            field.value !== undefined ? !field.value : true
                          }
                          disabled={disabled}
                          accent
                          onChange={(e) => {
                            const allow = e.target.checked
                            field.onChange(!allow)
                          }}
                        />
                      </div>
                    </BreakoutTooltip>
                  )
                }}
              />
            )
          })}

          <Controller
            control={control}
            disabled={!!disableGroupingReason}
            name="groupingType"
            render={({ field }) => (
              <BreakoutTooltip
                enabled={!!disableGroupingReason}
                placement="right"
                className="whitespace-pre-wrap"
                content={disableGroupingReason ?? ''}
              >
                <div className="flex w-full flex-row items-center justify-between">
                  <strong
                    className={classNames('text-body-medium', {
                      'text-on-surface-var': !!disableGroupingReason,
                    })}
                  >
                    {tt.instructor_assignment.configure_assignment_dialog.auto_assign_students()}
                  </strong>

                  <BreakoutSwitch
                    initialValue={
                      field.value === AssignmentGroupingType.automaticRandom
                    }
                    disabled={!!disableGroupingReason || readOnly}
                    accent
                    onChange={(e) => {
                      const groupingType = e.target.checked
                        ? AssignmentGroupingType.automaticRandom
                        : AssignmentGroupingType.manual

                      field.onChange(groupingType)
                    }}
                  />
                </div>
              </BreakoutTooltip>
            )}
          />
        </div>
      )}
      <a
        className={classNames('mx-auto mb-1 p-1.5 text-body-medium underline', {
          'cursor-not-allowed text-on-surface-var': !!disableGroupingReason,
          'cursor-pointer text-on-surface': !disableGroupingReason,
        })}
        tabIndex={0}
        onClick={() => {
          if (disableGroupingReason) return
          const isSimpleConfig =
            isSimpleStudentConfig || isSimpleInstructorConfig

          // if moving back to simple select and not simple config, set fields to default
          if (showCustomGrouping) {
            if (!isSimpleConfig) {
              setValue('forbidStudentGroupScheduling', false)
              setValue('forbidStudentGroupCreation', false)
              setValue('forbidStudentGroupSelection', false)
              setValue('groupingType', AssignmentGroupingType.manual)
            }
            return setValue('customGroupingSelected', false)
          }

          // if moving to advanced, leave all fields as is
          return setValue('customGroupingSelected', true)
        }}
        onKeyDown={composeClickHandlers(clickOnEnter, clickOnSpace)}
        data-testid="advanced-group-settings-toggle"
      >
        {showCustomGrouping
          ? tt.instructor_assignment.configure_assignment_dialog.return_to_simple_group_settings()
          : tt.instructor_assignment.configure_assignment_dialog.advanced_group_settings()}
      </a>
      <div className="mx-auto w-full max-w-[450px]">
        <AdminOnlyRing label="" ringClassName="mt-2" onlyShowIf={false}>
          <Controller
            control={control}
            name="assignmentType"
            render={({ field, fieldState }) => (
              <BreakoutSelect
                {...field}
                error={fieldState.error}
                disabled={readOnly}
                required
                kind="secondary"
                label={tt.instructor_library.assignment_type()}
                name="assignmentType"
                options={[
                  {
                    value: AssignmentType.ManyGroups,
                    label: tt.instructor_library.assignment_type_student_led(),
                  },
                  {
                    value: AssignmentType.OneGroup,
                    label:
                      tt.instructor_library.assignment_type_instructor_led(),
                  },
                ]}
              />
            )}
          />
        </AdminOnlyRing>

        <GroupingSizeInput
          form={form}
          minGroupSize={minGroupSize}
          maxGroupSize={maxGroupSize}
          disableGroupingReason={disableGroupingReason}
          readOnly={readOnly}
          roleGroupSizeConstraints={cubit.roleGroupSizeConstraints}
          roles={cubit.roles.models}
        />

        {!isForcedStage && (
          <PeerReviewsToggle
            control={control}
            setValue={setValue}
            groupingSize={groupingSize}
            peerReviewsEnabled={peerReviewsEnabled}
            isForcedStage={isForcedStage}
            readOnly={readOnly}
          />
        )}
      </div>
    </>
  )
}

export const PeerReviewsToggle = ({
  control,
  setValue,
  groupingSize,
  peerReviewsEnabled,
  isForcedStage,
  readOnly = false,
}: {
  control: Control<AssignmentFormValues>
  setValue: UseFormSetValue<AssignmentFormValues>
  groupingSize: number | undefined
  peerReviewsEnabled: boolean | undefined
  isForcedStage: boolean
  readOnly?: boolean
}) => {
  const { tt } = useTranslationTyped()
  const isPeerReviewDisabled = !isPeerReviewAllowed(groupingSize)

  useEffect(() => {
    // Automatically disable peer reviews if grouping size is set to 1.
    if (isPeerReviewAllowed(groupingSize)) return
    if (!peerReviewsEnabled) return
    if (isForcedStage) return // The peer reviews toggle isn't shown on the grouping stage if it's forced; we handle this conflict by a validation error instead

    setValue('peerReviewsEnabled', false, { shouldDirty: false })
  }, [groupingSize, isForcedStage, peerReviewsEnabled, setValue])

  return (
    <>
      <div className="my-5 h-[2px] w-full bg-outline-light" />

      <Controller
        control={control}
        name="peerReviewsEnabled"
        render={({ field }) => (
          <div className="flex flex-col">
            <BreakoutTooltip
              enabled={isPeerReviewDisabled}
              content={tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one()}
            >
              <div>
                <BreakoutSwitch
                  className="w-full justify-between gap-10"
                  testId="peer-reviews-enabled-switch"
                  label={
                    <div className="flex items-center gap-2">
                      <PeopleMultiple size={14} />
                      {tt.instructor_assignment.configure_assignment_dialog.configure_peer_reviews_label()}
                    </div>
                  }
                  labelClassName="text-label-medium font-bold !text-black"
                  labelProps={{ style: { paddingLeft: '0px' } }}
                  labelPosition="left"
                  initialValue={field.value ?? false}
                  disabled={readOnly || isPeerReviewDisabled}
                  accent
                  onChange={(e) => {
                    field.onChange(e.target.checked)
                  }}
                />
              </div>
            </BreakoutTooltip>

            <p className="pt-2.5 text-body-medium">
              {tt.instructor_assignment.configure_assignment_dialog.configure_peer_reviews_description()}
            </p>
          </div>
        )}
      />
    </>
  )
}
